// Edit / Sign PDF (FR-13, FR-14): the items added on top of pages and the
// maths between the page on screen and PDF space. Pure: no pdf-lib or fontkit,
// so the editor page stays light; annotate.ts does the drawing in the worker.
//
// Items use the page *as shown on screen*: points (1/72 inch), origin top-left,
// y down, after the page's own rotation and crop box — the same space as
// pdf.js's viewport at scale 1. `toPdf` maps that back to PDF user space.
import type { FontId } from "@/lib/pdf/fonts";

export type { FontId };
/** Font files for the Google fonts used, keyed by font id. */
export type FontFiles = Partial<Record<FontId, Uint8Array>>;

type Base = { id: string; /** 0-based page */ page: number };
export type Box = { x: number; y: number; width: number; height: number };

export type TextItem = Base & {
  kind: "text";
  x: number;
  y: number;
  text: string;
  font: FontId;
  size: number;
  color: string;
};
export type ImageItem = Base &
  Box & {
    kind: "image";
    /** PNG or JPG bytes (the worker gets bytes, the editor keeps a Blob). */
    bytes?: Uint8Array;
    blob?: Blob;
    format: "png" | "jpg";
  };
export type RectItem = Base &
  Box & {
    kind: "rect";
    stroke: string;
    strokeWidth: number;
    fill: string | null;
  };
export type WhiteoutItem = Base & Box & { kind: "whiteout" };
/** Line, pen and highlighter: points are relative to (x, y). */
export type StrokeItem = Base & {
  kind: "line" | "pen" | "highlight";
  x: number;
  y: number;
  points: number[];
  color: string;
  width: number;
};

export type Item = TextItem | ImageItem | RectItem | WhiteoutItem | StrokeItem;

export const LINE_HEIGHT = 1.2;
export const HIGHLIGHT_OPACITY = 0.35;

/** A page's visible box in PDF user space and its clockwise rotation. */
export type PageFrame = {
  x0: number;
  y0: number;
  width: number;
  height: number;
  rotation: 0 | 90 | 180 | 270;
};

/** The page's size as shown (rotation swaps width and height). */
export function shownSize(f: PageFrame) {
  return f.rotation % 180 === 0
    ? { width: f.width, height: f.height }
    : { width: f.height, height: f.width };
}

/** Screen point (top-left origin, y down) → PDF user space (y up). */
export function toPdf(f: PageFrame, vx: number, vy: number) {
  const x1 = f.x0 + f.width;
  const y1 = f.y0 + f.height;
  switch (f.rotation) {
    case 90:
      return { x: f.x0 + vy, y: f.y0 + vx };
    case 180:
      return { x: x1 - vx, y: f.y0 + vy };
    case 270:
      return { x: x1 - vy, y: y1 - vx };
    default:
      return { x: f.x0 + vx, y: y1 - vy };
  }
}

/** Characters the built-in PDF fonts can write (Windows-1252). Anything else
 *  — e.g. Hindi — is turned into a picture of the text before saving. */
export function isWinAnsi(text: string): boolean {
  const extra = "€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ";
  for (const ch of text) {
    const code = ch.codePointAt(0)!;
    if (code === 10 || code === 13) continue;
    if ((code >= 0x20 && code <= 0x7e) || (code >= 0xa0 && code <= 0xff)) {
      continue;
    }
    if (!extra.includes(ch)) return false;
  }
  return true;
}

/** An item before it gets an id (Omit that keeps each kind's own fields). */
export type NewItem = Item extends infer T
  ? T extends Item
    ? Omit<T, "id">
    : never
  : never;
